/**
 * Notification Context
 *
 * Provides global access to notification bell data across all pages.
 * The subscription is established once in AuthenticatedLayout,
 * and the data is available to any component that needs it.
 *
 * This enables the NotificationBell to appear in GlobalHeader on all pages,
 * not just pages that explicitly opt into notifications.
 */

import { createContext, useContext, ReactNode } from 'react';
import type { NotificationItem, NotificationSource } from '@/types/notification.types';

interface NotificationContextValue {
  /** All notifications from Firestore subscription */
  notifications: NotificationItem[];
  /** Count of unread notifications */
  unreadCount: number;
  /** Mark a single notification as read */
  markAsRead: (notificationId: string, source?: NotificationSource) => Promise<void>;
  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;
  /** Whether the notification system is available (user is authenticated, etc.) */
  isAvailable: boolean;
  /** Handle notification click from bell dropdown */
  onNotificationClick?: (notification: NotificationItem) => void;
  /** Open the settings modal for notification preferences */
  onOpenSettings?: () => void;
  /** Open the notification history modal */
  onOpenHistory?: () => void;
}

const defaultValue: NotificationContextValue = {
  notifications: [],
  unreadCount: 0,
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  isAvailable: false,
};

const NotificationContext = createContext<NotificationContextValue>(defaultValue);

export const NotificationProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: NotificationContextValue;
}) => {
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Hook to access global notification data
 * Use this in components that need to display or interact with notifications
 */
export const useNotificationContext = () => {
  return useContext(NotificationContext);
};
